import React, { useMemo, useState, useRef } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import ViewEditor from "../../components/common/ViewEditor";
import { HiArrowUpTray } from "react-icons/hi2";
import { IoDocumentAttachOutline } from "react-icons/io5";
import { FaPencil, FaRegFile } from "react-icons/fa6";

const QUESTION_TYPES = {
    MCQ: "MCQ",
    DESCRIPTIVE: "Descriptive",
    SUBMISSION: "Submission",
    SUBQUESTION: "Sub Question",
};

const MCQ_ANSWER_TYPE = {
    ONE: 1,
    MULTIPLE: 2,
};

function safeParseDraftList(content) {
    try {
        const parsed = JSON.parse(content || "[]");
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function toEditorState(raw) {
    try {
        if (!raw) return EditorState.createEmpty();
        return EditorState.createWithContent(convertFromRaw(raw));
    } catch {
        return EditorState.createEmpty();
    }
}

function renderDraft(raw, className = "") {
    const editorState = toEditorState(raw);
    return (
        <div className={className}>
            <ViewEditor editorState={editorState} />
        </div>
    );
}

function AnswerEditor({
    questionId,
    value,
    toolbarVisible,
    onEditorAnswerChange,
    minHeight = "180px",
    maxHeight = "260px",
}) {
    const [editorState, setEditorState] = useState(() => toEditorState(value));
    const debounceRef = React.useRef(null);

    React.useEffect(() => {
        setEditorState(toEditorState(value));
    }, [value]);

    const handleChange = (nextEditorState) => {
        setEditorState(nextEditorState);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            const rawAnswer = convertToRaw(nextEditorState.getCurrentContent());
            onEditorAnswerChange(questionId, rawAnswer);
        }, 2000);
    };

    React.useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <Editor
            placeholder="Add your answer here..."
            editorStyle={{ minHeight, maxHeight, overflow: "auto" }}
            editorState={editorState}
            onEditorStateChange={handleChange}
            toolbar={
                toolbarVisible
                    ? {
                        fontFamily: {
                            options: [
                                "Arial",
                                "Georgia",
                                "Impact",
                                "Tahoma",
                                "Times New Roman",
                                "Verdana",
                                "Kushpoo",
                                "Baamini",
                                "FMAbhaya",
                            ],
                        },
                    }
                    : {}
            }
            toolbarClassName={toolbarVisible ? "vertical-toolbar" : "hide-toolbar"}
        />
    );
}

export default function ExamQuestionRenderer({
    question,
    displayNumber,
    showPassage = true,
    answers,
    reviewFlags,
    onAnswerChange,
    onReviewToggle,
    subQuestionMap,
    onEditorAnswerChange,
    onFileUpload,
    onFileDelete,
    uploadingFiles = {},
    onFileDownload = () => { },
}) {
    const questionId = question.question_id || question.id;
    const draftParts = useMemo(
        () => safeParseDraftList(question.content),
        [question.content]
    );

    const questionContent = draftParts[0];
    const optionContents = draftParts.slice(1);
    const answerValue = answers[questionId];
    const isReviewed = Boolean(reviewFlags[questionId]);
    const [toolbarVisible, setToolbarVisible] = useState({});
    const renderPassage = () => {
        if (!showPassage || !question?.passage_content) return null;

        const passageParts = safeParseDraftList(question.passage_content);
        return (
            <div className="exam-question-passage">
                {renderDraft(passageParts[0])}
            </div>
        );
    };

    const employeeType = sessionStorage.getItem("employeeType");
    const userType = sessionStorage.getItem("userType");
    const isDeopOrMasad = employeeType === "DEOP" || employeeType === "MASAD" || userType === "DEOP" || userType === "MASAD";

    const renderHeader = () => (
        <div className="exam-question-card__header exam-question-card__header--main">
            <div className="exam-question-main-title">
                <span className="exam-question-main-number">Q{displayNumber}</span>

                <div className="exam-question-main-content">
                    {/* {question.is_optional && (
                        <span className="badge badge-primary mr-2">Optional</span>
                    )} */}
                    {renderDraft(questionContent, "exam-question-card__draft-title")}
                </div>
            </div>

            {!isDeopOrMasad && (
                <label className="exam-question-card__review">
                    <input
                        type="checkbox"
                        checked={isReviewed}
                        onChange={() => onReviewToggle(questionId)}
                    />
                    <span />
                    Mark for Review
                </label>
            )}
        </div>
    );

    const renderMcq = () => {
        const selectedAnswers = Array.isArray(answerValue) ? answerValue : [];
        const isSingleAnswer = Number(question.answer_type_id) === MCQ_ANSWER_TYPE.ONE;

        return (
            <>
                {renderPassage()}
                {renderHeader()}

                <div className="row">
                    {optionContents.map((optionRaw, optionIndex) => {
                        const checked = selectedAnswers.includes(optionIndex);

                        return (
                            <div
                                className="col-12 col-md-6"
                                key={`${questionId}-option-${optionIndex}`}
                            >
                                <label className="exam-option">
                                    <input
                                        type={isSingleAnswer ? "radio" : "checkbox"}
                                        name={`question-${questionId}`}
                                        checked={checked}
                                        onChange={() => {
                                            if (isSingleAnswer) {
                                                onAnswerChange(questionId, [optionIndex]);
                                                return;
                                            }

                                            const nextAnswers = checked
                                                ? selectedAnswers.filter((item) => item !== optionIndex)
                                                : [...selectedAnswers, optionIndex];

                                            onAnswerChange(questionId, nextAnswers);
                                        }}
                                    />
                                    <span className="exam-option__indicator" />
                                    <span className="exam-option__text">
                                        {renderDraft(optionRaw)}
                                    </span>
                                </label>
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    const renderDescriptive = () => {
        //const editorState = toEditorState(answerValue);
        const answerObject =
            answerValue?.text || answerValue?.files
                ? answerValue
                : { text: answerValue || null, files: [] };

        // const editorState = toEditorState(answerObject.text);
        const uploadedFiles = Array.isArray(answerObject.files) ? answerObject.files : [];
        const isUploading = Boolean(uploadingFiles[questionId]);
        const inputId = `file-input-${questionId}`;

        return (
            <>
                {renderPassage()}
                {renderHeader()}

                <div className="exam-question-card__tools">
                    {/* <button type="button" className="exam-paper-chip">
                        <HiArrowUpTray aria-hidden="true" />
                        Attach files
                    </button> */}

                    <input
                        id={inputId}
                        type="file"
                        style={{ display: "none" }}
                        onChange={(event) => onFileUpload(event, questionId)}
                    />

                    <div className="d-flex mb-3" style={{ gap: "8px" }}>
                        <button
                            type="button"
                            //className="btn btn-sm exam-action-btn"
                            className="exam-question-tool-btn"
                            disabled={isUploading}
                            onClick={() => document.getElementById(inputId)?.click()}
                        >
                            <IoDocumentAttachOutline style={{ fontSize: 13, marginRight: 4 }} />
                            {isUploading ? "Uploading..." : "Attach files"}
                        </button>

                        <button
                            type="button"
                            //className="btn btn-sm exam-action-btn"
                            className="exam-question-tool-btn"
                            onClick={() =>
                                setToolbarVisible((prev) => ({
                                    ...prev,
                                    [questionId]: !prev[questionId],
                                }))
                            }
                        >
                            <FaPencil style={{ fontSize: 11, marginRight: 4 }} />
                            {toolbarVisible[questionId] ? "Disable toolbar" : "Enable toolbar"}
                        </button>
                    </div>

                    {uploadedFiles.length > 0 && (
                        <div className="mb-2">
                            {uploadedFiles.map((fileName) => (
                                <div
                                    key={fileName}
                                    className="uploaded-file-card d-flex align-items-center mb-2"
                                >
                                    <div className="file-icon">
                                        <FaRegFile />
                                    </div>

                                    <div className="file-meta flex-grow-1 ms-3">
                                        <button
                                            type="button"
                                            className="file-title file-title--button"
                                            onClick={() => onFileDownload(questionId, fileName)}
                                        >
                                            {fileName}
                                        </button>
                                        <span className="file-subtitle">Uploaded</span>
                                    </div>

                                    <button
                                        type="button"
                                        className="exam-question-tool-btn"
                                        onClick={() => onFileDelete(questionId, fileName)}
                                        disabled={isUploading}
                                        title="Delete attachment"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <AnswerEditor
                        questionId={questionId}
                        value={answerObject.text}
                        toolbarVisible={Boolean(toolbarVisible[questionId])}
                        onEditorAnswerChange={onEditorAnswerChange}
                    />
                </div>
            </>
        );
    };

    // const renderSubmission = () => {
    //     const files = Array.isArray(answerValue) ? answerValue : [];

    //     return (
    //         <>
    //             {renderPassage()}
    //             {renderHeader()}

    //             <div className="exam-question-card__tools">
    //                 <button type="button" className="exam-paper-chip">
    //                     <HiArrowUpTray aria-hidden="true" />
    //                     Attach files
    //                 </button>

    //                 {files.length > 0 && (
    //                     <ul className="exam-submission-list">
    //                         {files.map((fileName) => (
    //                             <li key={fileName}>{fileName}</li>
    //                         ))}
    //                     </ul>
    //                 )}
    //             </div>
    //         </>
    //     );
    // };

    const renderSubmission = () => {
        return renderDescriptive();
    };
    const renderSubQuestions = () => {
        const subQuestionIds = question.sub_questions
            ? question.sub_questions
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean)
            : [];

        return (
            <>
                {renderPassage()}
                {renderHeader()}

                <div className="exam-subquestion-list">
                    {subQuestionIds.map((subQuestionId, subIndex) => {
                        const subQuestion = subQuestionMap[subQuestionId];
                        if (!subQuestion) return null;

                        const subId = subQuestion.question_id || subQuestion.id;
                        const subDraftParts = safeParseDraftList(subQuestion.content);
                        //const subEditorState = toEditorState(answers[subId]);
                        const subAnswerValue = answers[subId];
                        const subAnswerObject =
                            subAnswerValue?.text || subAnswerValue?.files
                                ? subAnswerValue
                                : { text: subAnswerValue || null, files: [] };

                        //const subEditorState = toEditorState(subAnswerObject.text);

                        return (
                            <div className="exam-subquestion-card" key={subId}>
                                <div className="exam-subquestion-title">
                                    <span className="exam-subquestion-number">{subIndex + 1}.</span>

                                    <div className="exam-subquestion-content">
                                        {subQuestion.is_optional && (
                                            <span className="badge badge-primary mr-2">Optional</span>
                                        )}
                                        {renderDraft(subDraftParts[0])}
                                    </div>
                                </div>

                                {(() => {
                                    const subAnswerObject =
                                        answers[subId]?.text || answers[subId]?.files
                                            ? answers[subId]
                                            : { text: answers[subId] || null, files: [] };

                                    const subUploadedFiles = Array.isArray(subAnswerObject.files)
                                        ? subAnswerObject.files
                                        : [];

                                    const subIsUploading = Boolean(uploadingFiles[subId]);
                                    const subInputId = `file-input-${subId}`;

                                    return (
                                        <>
                                            <input
                                                id={subInputId}
                                                type="file"
                                                style={{ display: "none" }}
                                                onChange={(event) => onFileUpload(event, subId)}
                                            />

                                            <div className="d-flex mb-3" style={{ gap: "8px" }}>
                                                <button
                                                    type="button"
                                                    className="exam-question-tool-btn"
                                                    disabled={subIsUploading}
                                                    onClick={() => document.getElementById(subInputId)?.click()}
                                                >
                                                    <IoDocumentAttachOutline style={{ fontSize: 13, marginRight: 4 }} />
                                                    {subIsUploading ? "Uploading..." : "Attach files"}
                                                </button>

                                                <button
                                                    type="button"
                                                    className="exam-question-tool-btn"
                                                    onClick={() =>
                                                        setToolbarVisible((prev) => ({
                                                            ...prev,
                                                            [subId]: !prev[subId],
                                                        }))
                                                    }
                                                >
                                                    <FaPencil style={{ fontSize: 11, marginRight: 4 }} />
                                                    {toolbarVisible[subId] ? "Disable toolbar" : "Enable toolbar"}
                                                </button>
                                            </div>

                                            {subUploadedFiles.length > 0 && (
                                                <div className="mb-2">
                                                    {subUploadedFiles.map((fileName) => (
                                                        <div
                                                            key={fileName}
                                                            className="uploaded-file-card d-flex align-items-center mb-2"
                                                        >
                                                            <div className="file-icon">
                                                                <FaRegFile />
                                                            </div>

                                                            <div className="file-meta flex-grow-1 ms-3">
                                                                <button
                                                                    type="button"
                                                                    className="file-title file-title--button"
                                                                    onClick={() => onFileDownload(subId, fileName)}
                                                                >
                                                                    {fileName}
                                                                </button>
                                                                <span className="file-subtitle">Uploaded</span>
                                                            </div>

                                                            <button
                                                                type="button"
                                                                className="exam-question-tool-btn"
                                                                onClick={() => onFileDelete(subId, fileName)}
                                                                disabled={subIsUploading}
                                                                title="Delete attachment"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                                <AnswerEditor
                                    questionId={subId}
                                    value={subAnswerObject.text}
                                    toolbarVisible={Boolean(toolbarVisible[subId])}
                                    onEditorAnswerChange={onEditorAnswerChange}
                                    minHeight="160px"
                                    maxHeight="220px"
                                />
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    return (
        <article className="exam-question-card">
            {question.question_type === QUESTION_TYPES.MCQ && renderMcq()}
            {question.question_type === QUESTION_TYPES.DESCRIPTIVE && renderDescriptive()}
            {question.question_type === QUESTION_TYPES.SUBMISSION && renderSubmission()}
            {question.question_type === QUESTION_TYPES.SUBQUESTION && renderSubQuestions()}
        </article>
    );
}